/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { useShowPickupAvailable } from './use-pickup-filter';
import type { ShopperSearch } from '@/scapi';
import DynamicImageProvider from '@/providers/dynamic-image';
import { ProductTile, ProductTileProvider } from '@/components/product-tile';
import { ProductTileSkeleton } from '@/components/category-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import {
    getProductGridClassName,
    getProductGridImageWidths,
    PRODUCT_GRID_VIEW_DEFAULT,
    type ProductGridView,
} from '@/lib/product-grid-view';
import type { ColorCountMap } from './use-product-color-counts';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

function NoProductsMessage({ criticalSize, nonCriticalSize }: { criticalSize: number; nonCriticalSize: number }) {
    const { t } = useTranslation('common');

    if (criticalSize > 0 || nonCriticalSize > 0) {
        return null;
    }
    return (
        <div className="col-span-full text-center py-12">
            <p className="text-sm text-muted-foreground">{t('noProductsFound')}</p>
        </div>
    );
}

function NonCriticalContent({
    products,
    criticalSize,
    responsiveImageWidths,
    handleProductClick,
    enableQuickAdd,
    colorCountsData,
    useColorCountData,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable,
}: {
    products: ProductSearchHit[];
    criticalSize: number;
    responsiveImageWidths: string[];
    handleProductClick?: (product: ProductSearchHit) => void;
    colorCountsData: ColorCountMap;
    useColorCountData?: boolean;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
    enableQuickAdd?: boolean;
}) {
    const colorCountStatus = colorCountsData.status ?? 'idle';

    return (
        <DynamicImageProvider value={{ widths: responsiveImageWidths }}>
            {products.map((product) => (
                <ProductTile
                    key={product.productId}
                    product={product}
                    handleProductClick={handleProductClick}
                    showNavigationArrows
                    enableQuickAdd={enableQuickAdd}
                    // @sfdc-extension-line SFDC_EXT_BOPIS
                    showPickupAvailable={showPickupAvailable}
                    colorCountData={colorCountsData[product.productId ?? '']}
                    useColorCountData={useColorCountData}
                    colorCountStatus={colorCountStatus}
                />
            ))}
            <NoProductsMessage criticalSize={criticalSize} nonCriticalSize={products.length} />
        </DynamicImageProvider>
    );
}

function ProductGridSkeleton({
    count = 8,
    gridView = PRODUCT_GRID_VIEW_DEFAULT,
}: {
    count?: number;
    gridView?: ProductGridView;
}) {
    return (
        <div className={getProductGridClassName(gridView)}>
            {Array.from({ length: count }, (_, index) => (
                <div key={`grid-skeleton-${index}`} className="space-y-3">
                    <Skeleton className="aspect-4/5 w-full" />
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-5 w-20" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * ProductGrid renders product tiles in a responsive grid layout, wrapping all tiles in a shared
 * context provider to reduce hydration overhead. Instead of each tile initializing its own hooks
 * (navigate, config, translation, currency), the provider initializes them once and shares them
 * via context.
 *
 * The grid accepts synchronous data for both critical (above-the-fold) and non-critical
 * (below-the-fold) products. Critical product images are loaded with high priority and eagerly.
 * For deferred loading of non-critical products, use DeferredProductGrid.
 */
export default function ProductGrid({
    critical,
    nonCritical,
    hasRefinementsPanel = true,
    gridView = PRODUCT_GRID_VIEW_DEFAULT,
    handleProductClick,
    isLoading = false,
    skeletonCount,
    colorCountsByProductId = {},
    useColorCountData = false,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable: showPickupAvailableProp,
}: {
    critical?: ProductSearchHit[];
    nonCritical?: ProductSearchHit[];
    hasRefinementsPanel?: boolean;
    gridView?: ProductGridView;
    handleProductClick?: (product: ProductSearchHit) => void;
    isLoading?: boolean;
    skeletonCount?: number;
    colorCountsByProductId?: ColorCountMap;
    useColorCountData?: boolean;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}): ReactElement {
    const config = useConfig();
    const enableQuickAdd = config.global.productListing.enableQuickAdd ?? true;
    const criticalData = critical ?? [];
    const l = criticalData.length;
    const colorCountStatus = colorCountsByProductId?.status ?? (useColorCountData ? 'loading' : 'idle');
    const responsiveImageWidths = getProductGridImageWidths(gridView, hasRefinementsPanel);
    const gridClassName = getProductGridClassName(gridView);

    // Initialize the `<DynamicImageProvider/>` behavior for the scope of this grid.
    // Out-of-the-box we make sure that the product images of all products considered critical (displayed inside a
    // `<DynamicImage/>` component) should be loaded eagerly with high priority.
    const hasSource = useCallback(() => true, []);

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const pickupFromUrl = useShowPickupAvailable();
    const showPickupAvailable = showPickupAvailableProp ?? pickupFromUrl;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    const loadingSkeletonCount = Math.max(criticalData.length + (nonCritical?.length ?? 0), 4);

    if (isLoading) {
        return (
            <ProductTileProvider>
                <div data-testid="product-grid-loading-state" aria-busy>
                    <ProductGridSkeleton count={loadingSkeletonCount} gridView={gridView} />
                </div>
            </ProductTileProvider>
        );
    }

    return (
        <ProductTileProvider>
            <div className={gridClassName}>
                {l > 0 && (
                    <DynamicImageProvider value={{ hasSource, widths: responsiveImageWidths }}>
                        {criticalData.map((product) => (
                            <ProductTile
                                key={product.productId}
                                product={product}
                                handleProductClick={handleProductClick}
                                showNavigationArrows
                                enableQuickAdd={enableQuickAdd}
                                colorCountData={colorCountsByProductId[product.productId ?? '']}
                                useColorCountData={useColorCountData}
                                colorCountStatus={colorCountStatus}
                                // @sfdc-extension-line SFDC_EXT_BOPIS
                                showPickupAvailable={showPickupAvailable}
                            />
                        ))}
                    </DynamicImageProvider>
                )}
                {nonCritical && nonCritical.length > 0 && (
                    <NonCriticalContent
                        products={nonCritical}
                        criticalSize={l}
                        responsiveImageWidths={responsiveImageWidths}
                        handleProductClick={handleProductClick}
                        enableQuickAdd={enableQuickAdd}
                        colorCountsData={colorCountsByProductId}
                        useColorCountData={useColorCountData}
                        // @sfdc-extension-line SFDC_EXT_BOPIS
                        showPickupAvailable={showPickupAvailable}
                    />
                )}
                {(skeletonCount ?? 0) > 0 &&
                    Array.from({ length: skeletonCount ?? 0 }, (_, i) => <ProductTileSkeleton key={`skeleton-${i}`} />)}
                {!skeletonCount && <NoProductsMessage criticalSize={l} nonCriticalSize={nonCritical?.length ?? 0} />}
            </div>
        </ProductTileProvider>
    );
}
